import { forwardRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@ds/lib/utils";

/**
 * Dialog — overlay terpusat untuk konfirmasi aksi berisiko (void, hapus) &
 * form pendek. State Shadow Rule: shadow muncul saat elevated. Backdrop blur
 * fungsional (memisahkan), bukan glassmorphism dekoratif.
 */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

/**
 * Enter di dalam field teks dialog -> klik tombol aksi utama (primary), agar
 * kasir bisa simpan/bayar tanpa mouse (full keyboard). Diabaikan bila:
 * Shift+Enter, IME aktif, sudah di-handle komponen lain (mis. SearchSelect
 * yang preventDefault), atau fokus bukan di <input> teks. Set
 * `enterSubmits={false}` untuk menonaktifkan per-dialog.
 */
function enterClicksPrimary(event: React.KeyboardEvent<HTMLDivElement>) {
  if (event.defaultPrevented || event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) {
    return;
  }
  const target = event.target as HTMLElement;
  if (target.tagName !== "INPUT") return;
  const inputType = (target as HTMLInputElement).type;
  if (inputType === "checkbox" || inputType === "radio") return;
  const primaries = event.currentTarget.querySelectorAll<HTMLButtonElement>(
    'button[data-variant="primary"]:not([disabled])',
  );
  const button = primaries[primaries.length - 1];
  if (button) {
    event.preventDefault();
    button.click();
  }
}

export const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideClose?: boolean;
    enterSubmits?: boolean;
  }
>(({ className, children, hideClose, enterSubmits = true, onKeyDown, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="z-backdrop fixed inset-0 bg-black/45 backdrop-blur-[2px] overlay-in" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "z-modal fixed left-1/2 top-1/2 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2",
        "rounded-lg border border-border bg-surface p-5 shadow-[var(--shadow-pop)] pop-in",
        "focus:outline-none",
        className,
      )}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (enterSubmits) enterClicksPrimary(event);
      }}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close
          className="absolute right-3 top-3 flex size-8 items-center justify-center rounded-md text-muted transition-colors hover:bg-faint hover:text-ink focus-visible:outline-none"
          aria-label="Tutup"
        >
          <X className="size-4" aria-hidden />
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
));
DialogContent.displayName = "DialogContent";

export function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("mb-4 flex flex-col gap-1 pr-8", className)} {...props} />;
}

export function DialogTitle({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn("text-lg font-semibold text-ink", className)}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn("text-sm text-muted", className)}
      {...props}
    />
  );
}

export function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}
